import { format, formatDistanceToNow, parseISO } from "date-fns";

/**
 * Date formatting helpers. The dashboard convention:
 *   - Relative ("2h ago") for recency, displayed by default
 *   - Absolute ("May 14, 2026 · 03:42") on hover via the `title` attr
 *
 * Always parse the ISO string in one place so timezone behavior stays
 * consistent across the app.
 */

export function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  try {
    return typeof iso === "string" ? parseISO(iso) : new Date(iso);
  } catch {
    return null;
  }
}

export function relative(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
}

export function absolute(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return "—";
  return format(d, "MMM d, yyyy · HH:mm");
}

export function shortDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return "—";
  return format(d, "MMM d");
}

export function dateRange(fromIso: string, toIso: string): string {
  const a = toDate(fromIso);
  const b = toDate(toIso);
  if (!a || !b) return "—";
  return `${format(a, "MMM d")} – ${format(b, "MMM d")}`;
}

// Compact percentage formatter: 78.5 → "78.5%", null/undefined → "—"
export function pct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  // Drop trailing .0 — "78.0%" looks fake-precise
  return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}%`;
}

export function pctDelta(curr: number | null | undefined, prev: number | null | undefined): {
  label: string;
  direction: "up" | "down" | "flat" | "unknown";
} {
  if (curr === null || curr === undefined || prev === null || prev === undefined) {
    return { label: "no prior week", direction: "unknown" };
  }
  const d = Math.round((curr - prev) * 10) / 10;
  if (d === 0) return { label: "no change vs last week", direction: "flat" };
  const sign = d > 0 ? "↑" : "↓";
  const abs = Math.abs(d) % 1 === 0 ? Math.abs(d).toFixed(0) : Math.abs(d).toFixed(1);
  return {
    label: `${sign} ${abs} pts vs last week`,
    direction: d > 0 ? "up" : "down",
  };
}
